import React, { createContext, useContext, ReactNode } from 'react';
import type { AutoPropsMap } from '../type_map';

const AutoPropsContext = createContext<AutoPropsMap | null>(null);

/**
 * Provider for useAutoProps hook that reads from AutoPropsContext.
 *
 * @example
 * ```tsx
 * <AutoPropsProvider props={autoProps}>
 *   <MyComponent />
 * </AutoPropsProvider>
 * ```
 */
export function AutoPropsProvider({
  children,
  props,
}: {
  children: ReactNode;
  props: AutoPropsMap;
}) {
  return (
    <AutoPropsContext.Provider value={props}>
      {children}
    </AutoPropsContext.Provider>
  );
}

function useAutoPropsByKey<K extends keyof AutoPropsMap>(key: K) {
  const context = useContext(AutoPropsContext);

  if (context === null) {
    throw new Error('useAutoProps must be used within AutoPropsProvider');
  }

  const props = context[key];

  if (props === undefined) {
    throw new Error(`AutoProps '${key}' not found in context`);
  }

  return props;
}

/**
 * React hook to access AutoProps values from context. Use this instead of getAutoProps
 * in React components, so that they can get the AutoProps values even during SSR.
 *
 * Must be used within an AutoPropsProvider.
 * (AutoPropsProvider is automatically added by registerForRails.)
 *
 * @param key - The key of the auto props to retrieve
 * @throws Error if used outside AutoPropsProvider or if props not found
 *
 * @example
 * ```tsx
 * const topics = useAutoProps('UserTopics');
 * ```
 */
export function useAutoProps<K extends keyof AutoPropsMap>(key: K) {
  return useAutoPropsByKey<K>(key);
}
